import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  X_SITE_HANDLE,
  LOCALE_TO_OG,
  buildCanonical,
  buildHreflangs,
} from "./seoConfig";

/**
 * Reusable SEO component for public marketing pages.
 *
 * Props (all optional):
 * - title:        Final page title (without site suffix)
 * - description:  Meta description (max ~160 chars)
 * - keywords:     Comma separated keywords or array
 * - image:        Absolute URL of OpenGraph image
 * - path:         Canonical path (defaults to current location)
 * - type:         OpenGraph type (website | article | product...)
 * - noindex:      If true, sets robots to noindex,nofollow
 * - jsonLd:       Single JSON-LD object or array of objects to embed
 */
const Seo = ({
  title,
  description,
  keywords,
  image,
  path,
  type = "website",
  noindex = false,
  jsonLd,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n?.language || "en").split("-")[0];
  const canonicalPath = path || location.pathname;

  const fullTitle = useMemo(() => {
    if (!title) return SITE_NAME;
    return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  }, [title]);

  const canonical = buildCanonical(canonicalPath);
  const hreflangs = buildHreflangs(canonicalPath);
  const ogLocale = LOCALE_TO_OG[lang] || "en_US";

  const keywordsValue = Array.isArray(keywords)
    ? keywords.filter(Boolean).join(", ")
    : keywords;

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywordsValue && <meta name="keywords" content={keywordsValue} />}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex,nofollow"
            : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"
        }
      />
      <meta name="author" content={SITE_NAME} />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="theme-color" content="#4cceac" />

      <link rel="canonical" href={canonical} />
      {hreflangs.map((h) => (
        <link
          key={h.hrefLang}
          rel="alternate"
          hrefLang={h.hrefLang}
          href={h.href}
        />
      ))}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={image || DEFAULT_OG_IMAGE} />
      <meta property="og:image:alt" content={fullTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={X_SITE_HANDLE} />
      <meta name="twitter:creator" content={X_SITE_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      {description && (
        <meta name="twitter:description" content={description} />
      )}
      <meta name="twitter:image" content={image || DEFAULT_OG_IMAGE} />

      {jsonLdArray.map((obj, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
